// src/portal-app/tabs/data/useInsightsData.js
//
// Data hook for the Insights tab landing (Task 7). Isolates every network
// call behind the shared { status, data, reload } envelope.
//
// Range: shared with every other Insights surface via useRangePreset() (M1
// of the W2 final review) — the preset survives pops/tab switches because
// its provider lives in PortalShell, so changing the range on e.g.
// FinancialPerformanceScreen also re-fetches this landing next time it
// mounts. rangeForPreset()/toLocalISO() only — never toISOString(), which
// reports the wrong calendar day for any user behind UTC.
//
// Field provenance:
//   - reportingService.getFinancialV2(start, end) (PRIMARY) supplies
//     revenue/revenueChangePct (the "Total Revenue" KPI, found by NAME —
//     kpis[] order is not contractual; a conditional "Rev vs Target" entry
//     appears only for some tenants) and sparkline (sections.revenue_trend).
//   - reportingService.getCostsV2(start, end) (SECONDARY) supplies
//     grossMargin/grossMarginChangePts from its "Gross Margin %" KPI —
//     /financial's "Profit Margin" is NET profit margin, a different
//     number, and must never stand in for gross margin. `change_absolute`
//     is the true point delta on this KPI (`change_percent` is a relative
//     % of the margin itself); grossMarginChangePts uses change_absolute.
//   - reportingService.getNarrativeV2(start, end) (SECONDARY) supplies
//     narrative/aiGenerated plus anomalies[] merged into `changes`. Cached
//     server-side per tenant+range+day, so calling it on a landing is safe.
//
// `changes[]` merges financial.insights[] (success→green, warning→amber,
// info/opportunity→blue) with narrative.anomalies[] (positive→green,
// warning→amber), sorts amber (warning) items first, and caps at 4.
// `changesMoreCount` carries how many were trimmed so the UI can render
// "and N more" instead of silently dropping them.
//
// Every call — primary and secondary alike — is fired in ONE
// Promise.allSettled batch. Only the primary's rejection sets
// status: 'error'; a failed secondary degrades only the field(s) it feeds.
import { useCallback, useEffect, useRef, useState } from 'react';
import { reportingService } from '../../../services/api';
import { rangeForPreset, toLocalISO } from '../../nav/dateRange';
import { useRangePreset } from '../../insights/rangeState';

const TOTAL_REVENUE = 'Total Revenue';
const GROSS_MARGIN = 'Gross Margin %';
const MAX_CHANGES = 4;

const INSIGHT_TONE = { success: 'green', warning: 'amber', info: 'blue', opportunity: 'blue' };
const ANOMALY_TONE = { positive: 'green', warning: 'amber' };

function findKpi(kpis, name) {
    return (Array.isArray(kpis) ? kpis.find((k) => k && k.name === name) : null) || null;
}

export function useInsightsData() {
    const [preset] = useRangePreset();
    const [status, setStatus] = useState('loading');
    const [data, setData] = useState(null);
    const seqRef = useRef(0);

    const range = rangeForPreset(preset);
    const startIso = toLocalISO(range.start);
    const endIso = toLocalISO(range.end);

    const load = useCallback(() => {
        seqRef.current += 1;
        const seq = seqRef.current;
        setStatus('loading');

        const calls = [
            { key: 'financial', promise: reportingService.getFinancialV2(startIso, endIso) },
            { key: 'costs', promise: reportingService.getCostsV2(startIso, endIso) },
            { key: 'narrative', promise: reportingService.getNarrativeV2(startIso, endIso) },
        ];

        Promise.allSettled(calls.map((c) => c.promise)).then((results) => {
            if (seq !== seqRef.current) return; // stale — a faster reload already won

            const byKey = {};
            calls.forEach((c, i) => { byKey[c.key] = results[i]; });

            const financialResult = byKey.financial;
            if (!financialResult || financialResult.status !== 'fulfilled') {
                setData(null);
                setStatus('error');
                return;
            }
            const financial = financialResult.value || {};
            const kpis = financial.kpis || [];
            const revKpi = findKpi(kpis, TOTAL_REVENUE);

            const costsResult = byKey.costs;
            const costsOk = !!(costsResult && costsResult.status === 'fulfilled');
            const gmKpi = costsOk ? findKpi((costsResult.value || {}).kpis, GROSS_MARGIN) : null;

            const narrativeResult = byKey.narrative;
            const narrativeOk = !!(narrativeResult && narrativeResult.status === 'fulfilled');
            const narrativePayload = narrativeOk ? (narrativeResult.value || {}) : null;

            const sections = financial.sections || {};
            const sparkline = (sections.revenue_trend || []).map((r) => Number(r.revenue) || 0);

            const insightItems = (financial.insights || []).map((ins, i) => ({
                id: `insight-${i}`,
                tone: INSIGHT_TONE[ins.type] || 'blue',
                text: ins.title || ins.description || '',
            }));
            const anomalyItems = ((narrativePayload && narrativePayload.anomalies) || []).map((an, i) => ({
                id: `anomaly-${i}`,
                tone: ANOMALY_TONE[an.type] || 'blue',
                text: an.message || '',
            }));
            const merged = [...insightItems, ...anomalyItems]
                .sort((a, b) => (b.tone === 'amber' ? 1 : 0) - (a.tone === 'amber' ? 1 : 0));
            const changes = merged.slice(0, MAX_CHANGES);
            const changesMoreCount = Math.max(0, merged.length - MAX_CHANGES);

            setData({
                revenue: revKpi ? Number(revKpi.value) || 0 : 0,
                revenueChangePct: (revKpi && revKpi.change_percent != null) ? Number(revKpi.change_percent) : null,
                grossMargin: gmKpi ? Number(gmKpi.value) || 0 : null,
                grossMarginChangePts: (gmKpi && gmKpi.change_absolute != null) ? Number(gmKpi.change_absolute) : null,
                sparkline,
                narrative: narrativePayload ? (narrativePayload.narrative || null) : null,
                aiGenerated: !!(narrativePayload && narrativePayload.ai_generated),
                changes,
                changesMoreCount,
            });
            setStatus('ready');
        });
    }, [startIso, endIso]);

    useEffect(() => { load(); }, [load]);

    return { status, data, reload: load };
}

export default useInsightsData;
